import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../Header';
import Footer from '../Footer';
import { ArrowLeft, Users, Heart, Loader2, Briefcase } from 'lucide-react';
import { Button } from '../ui/button';
import { API_URL, getImageUrl } from '../../lib/api';

const TeamPage = () => {
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchData = async () => {
      try {
        const teamRes = await fetch(`${API_URL}/api/team`);
        
        if (teamRes.ok) {
          const teamData = await teamRes.json();
          setTeam(teamData);
        }
      } catch (err) {
        console.error('Error fetching team:', err);
      }
      setLoading(false);
    };
    
    fetchData();
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 via-white to-emerald-50">
      <Header />
      
      <main className="pt-24 pb-16">
        {/* Hero Section */}
        <section className="relative py-16 overflow-hidden">
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-10 right-10 w-32 h-32 bg-sky-200 rounded-full blur-3xl" />
            <div className="absolute bottom-10 left-20 w-40 h-40 bg-emerald-200 rounded-full blur-3xl" />
          </div>
          
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative">
            <Link to="/" className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700 mb-8 group">
              <ArrowLeft className="w-4 h-4 transition-transform group-hover:-translate-x-1" />
              Back to Home
            </Link>
            
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-sky-100 rounded-2xl">
                <Users className="w-8 h-8 text-sky-600" />
              </div>
              <h1 className="text-4xl sm:text-5xl font-serif font-bold text-slate-800">
                Our Team
              </h1>
            </div>
            
            <p className="text-xl text-slate-600 leading-relaxed">
              Meet the dedicated team behind Creassion Resources.
            </p>
          </div>
        </section>

        {/* Team Members */}
        <section className="py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
              </div>
            ) : (
              <div className="grid sm:grid-cols-3 gap-8">
                {team.map((member, index) => (
                  <div
                    key={member.member_id || member.id || index}
                    className="bg-white rounded-3xl p-6 shadow-lg border border-slate-100 text-center hover:shadow-xl transition-shadow group"
                  >
                    {member.image_url ? (
                      <div className="w-24 h-24 mx-auto mb-4 rounded-full overflow-hidden group-hover:scale-110 transition-transform">
                        <img 
                          src={getImageUrl(member.image_url)} 
                          alt={member.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    ) : (
                      <div className="w-24 h-24 mx-auto mb-4 rounded-full bg-gradient-to-br from-emerald-100 to-sky-100 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <span className="text-4xl font-serif font-bold text-emerald-600">
                          {member.name.charAt(0)}
                        </span>
                      </div>
                    )}
                    <h3 className="text-xl font-serif font-bold text-slate-800 mb-1">
                      {member.name}
                    </h3>
                    <p className="text-emerald-600 font-medium mb-3">{member.role}</p>
                    {member.bio && <p className="text-sm text-slate-600">{member.bio}</p>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* Join the Team CTA - Links to Volunteer Page */}
        <section className="py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-gradient-to-r from-emerald-500 to-teal-500 rounded-3xl p-8 sm:p-12 text-center text-white">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-white/20 rounded-2xl mb-6">
                <Briefcase className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl sm:text-3xl font-serif font-bold mb-4">
                Want to Join Our Team?
              </h2>
              <p className="text-emerald-100 mb-8 max-w-2xl mx-auto">
                We're always looking for passionate volunteers to help us spread the love of Jesus to children through books, resources, and interactive activities. Join us to be part of a mission-driven team!
              </p>
              <div className="flex justify-center">
                <Button 
                  onClick={() => navigate('/volunteer')}
                  className="bg-white text-emerald-600 hover:bg-emerald-50 rounded-full px-8 py-6 text-lg font-semibold shadow-lg"
                  data-testid="view-volunteer-btn"
                >
                  <Briefcase className="w-5 h-5 mr-2" />
                  View Volunteer Opportunities
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section className="py-12">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-white rounded-3xl p-8 sm:p-12 text-center shadow-lg border border-slate-100">
              <Heart className="w-12 h-12 mx-auto mb-4 text-coral-400" />
              <h2 className="text-2xl sm:text-3xl font-serif font-bold text-slate-800 mb-4">
                Have Questions?
              </h2>
              <p className="text-slate-600 mb-8 max-w-2xl mx-auto">
                We'd love to hear from you! Reach out to learn more about our mission.
              </p>
              <Link to="/contact">
                <Button className="bg-emerald-500 hover:bg-emerald-600 text-white rounded-full px-8 py-6">
                  Contact Us
                </Button>
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default TeamPage;
